<script lang="ts" setup>
  import { computed } from 'vue'

  import { storeToRefs } from 'pinia'
  import { useAuthStore } from '@/stores/auth'

  const props = defineProps<{
    showSidebarMenu?: () => void
  }>()

  const authStore = useAuthStore()
  const { currentUser } = storeToRefs(authStore)

  const userName = computed(() => currentUser.value?.name)

  async function logout() {
    await authStore.logout()
  }
</script>

<template>
  <header
    class="sticky top-0 z-30 flex flex-nowrap bg-white h-14 text-center text-sm items-stretch justify-between divide-x divide-solid divide-mbrd"
  >
    <!-- Menu -->
    <div>
      <div
        class="cursor-pointer text-center"
        @click="props.showSidebarMenu?.()"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 12H22"
            stroke="black"
            stroke-width="1.2"
            stroke-linecap="square"
          />
          <path
            d="M2 7H22"
            stroke="black"
            stroke-width="1.2"
            stroke-linecap="square"
          />
          <path
            d="M2 17H22"
            stroke="black"
            stroke-width="1.2"
            stroke-linecap="square"
          />
        </svg>
      </div>
    </div>

    <!-- Logo -->
    <div class="mr-auto">
      <router-link to="/blocks">
        <svg
          width="118"
          height="24"
          viewBox="0 0 118 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M46.6357 0V3.98873H34.4802V9.6H45.8569V13.5887H34.4802V20.0113H47.0081V24H29.7398V0H46.6357ZM19.8421 3.55316L17.0703 6.71871C15.6731 5.59893 14.5013 4.85599 13.5548 4.48991C12.5858 4.10229 11.4478 3.90848 10.1408 3.90848C8.81121 3.90848 7.74079 4.16689 6.92953 4.68372C6.1408 5.20054 5.74644 5.87887 5.74644 6.71871C5.74644 7.2786 5.93799 7.74159 6.32108 8.10767C6.72671 8.47376 7.30136 8.76447 8.04501 8.97981C8.78867 9.19516 9.97176 9.48587 11.5943 9.85195C14.7943 10.6057 17.0027 11.4347 18.2196 12.3392C19.4365 13.2436 20.0787 14.6433 20.1463 16.5384C20.1463 18.7349 19.2787 20.533 17.5435 21.9327C15.8309 23.3109 13.5323 24 10.6478 24C8.55205 24 6.69291 23.6878 5.07039 23.0633C3.49293 22.4172 1.8028 21.319 0 19.7685L2.83942 16.5707C4.48448 17.9489 5.83658 18.8641 6.89573 19.3163C8.04502 19.8116 9.32951 20.0592 10.7492 20.0592C12.1689 20.0592 13.307 19.7793 14.1633 19.2194C15.0196 18.638 15.4478 17.8627 15.4478 16.8937C15.4478 16.3123 15.2562 15.8385 14.8731 15.4724C14.5126 15.1279 14.0281 14.8587 13.4196 14.6649C12.8337 14.4495 11.8647 14.2019 10.5126 13.9219L9.36332 13.6635C6.18587 12.9314 4.01124 12.0808 2.83942 11.1117C1.66759 10.1211 1.08168 8.69987 1.08168 6.84792C1.08168 4.90983 1.93801 3.28399 3.65068 1.97039C5.36334 0.6568 7.56051 2.86102e-06 10.2422 2.86102e-06C12.2253 2.86102e-06 13.9154 0.269182 15.3126 0.807539C16.7548 1.3459 18.2647 2.26111 19.8421 3.55316ZM73.4975 3.98873V0H56.6016V24H73.8699V20.0113H61.3419V13.5887H72.7187V9.6H61.3419V3.98873H73.4975ZM99.0356 0H103.61V24H98.5984L91.8717 13.1831C89.9883 10.1183 88.6429 7.85352 87.8357 6.38873C87.9703 9.20563 88.0375 12.3155 88.0375 15.7183V24H83.4634V0H88.5757L94.9996 10.3437L95.9077 11.8648C97.2979 14.1183 98.3966 15.9549 99.2038 17.3746C99.0917 15.0085 99.0356 12.1239 99.0356 8.72113V0ZM118 19.2H113.203V24H118V19.2Z"
            fill="black"
          />
        </svg>
      </router-link>
    </div>

    <!-- Website -->
    <div>
      <a href="https://seenstore.ru/" target="_blank" rel="noopener noreferrer">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.9291 11.0711L20.0001 4M20.0001 4H15.7575M20.0001 4V8.24264M10 6.00001H6C4.89543 6.00001 4 6.89544 4 8.00001V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14"
            stroke="black"
            stroke-width="1.2"
            stroke-linecap="square"
          />
        </svg>
        <span>Перейти на сайт</span>
      </a>
    </div>

    <!-- User -->
    <div v-if="userName">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="7" r="4" stroke="black" stroke-width="1.2" />
        <path
          d="M17.25 14H6.75C5.7835 14 5 14.7835 5 15.75C5 18.6495 7.35051 21 10.25 21H12H13.75C16.6495 21 19 18.6495 19 15.75C19 14.7835 18.2165 14 17.25 14Z"
          stroke="black"
          stroke-width="1.2"
        />
      </svg>
      <span>{{ userName }}</span>
    </div>

    <!-- Logout -->
    <div>
      <button class="appearance-none" @click="logout">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 12H21M21 12L18 9M21 12L18 15"
            stroke="black"
            stroke-width="1.2"
            stroke-linecap="square"
          />
          <path
            d="M15 3H6C4.89543 3 4 3.89543 4 5V19C4 20.1046 4.89543 21 6 21H15"
            stroke="black"
            stroke-width="1.2"
            stroke-linecap="square"
          />
        </svg>
      </button>
    </div>
  </header>
</template>

<style scoped>
  header > div {
    border-color: theme('colors.mbrd');
    white-space: nowrap;
    line-height: 52px;
    padding-left: 24px;
    padding-right: 24px;
  }

  header > div svg {
    display: inline-block;
    vertical-align: middle;
  }

  header > div span {
    display: inline-block;
    vertical-align: middle;
  }

  header > div svg + span {
    margin-left: 8px;
  }

  header > div > a {
    display: inline-block;
    width: 100%;
    height: 100%;
  }
</style>
